import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ExternalLink, LifeBuoy, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
  Toolbar,
} from '@/ui/Panel';
import { Callout, Consequences } from '@/ui/Callout';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, MoneyInput, SearchInput, Select, Textarea } from '@/ui/Field';
import { AmountRow } from '@/ui/Receipt';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import {
  isUuid,
  parseRecoveryCart,
  parseRecoveryTenders,
  recoveryBalanceDue,
  type RecoveryCartLine,
} from '@/lib/api/mappers';
import { useAtLeast } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import { cn } from '@/lib/cn';
import { dateTime, humanize, money, num, qty as fmtQty, smartDate } from '@/lib/format';
import type {
  AdminRecoveryListResponse,
  AdminRecoveryRow,
  FinalizeRecoveryResponse,
  PaginatedResponse,
  RecoveryStatus,
  RecoveryLookupResponse,
} from '@/lib/api/types';
import { asOptionalText } from '@/lib/searchParams';
import {
  completedRecoveryTransactionId,
  parseRecoveryLifecycleConflict,
} from '@/features/recovery/conflicts';
import { recoverySnapshotKey, verifiedRecoveryLookup } from '@/features/recovery/lookup';

/**
 * Stranded carts — the most dangerous screen in the console.
 *
 * A recovery record is a cart a register parked: suspended on purpose, or left
 * mid-payment when a card bridge timed out and nobody knows whether the money
 * captured. Two things can be done with one:
 *
 *   **Finalize** creates a real transaction, takes a tender, moves inventory
 *   and can post to a house account. Getting it wrong double-charges a
 *   customer. So: manager only, every return line needs an explicit restock
 *   decision, the amount has to settle the record exactly, and recognized 409
 *   conflicts become non-retry outcomes. Some prove only that an existing
 *   transaction uses the cart identity, so the UI stops and asks for
 *   reconciliation instead of guessing that money landed.
 *
 *   **Void** closes the recovery after verified no capture or a verified
 *   refund/reversal. It moves no money and retains payment evidence. Applied
 *   tenders require typed confirmation; every closure needs an outcome note.
 *
 * Nothing here is optimistic. Every mutation waits for the server and then
 * re-reads the record.
 */

type StatusFilter = 'all' | 'active' | 'payment_pending' | 'recalled';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All open' },
  { value: 'payment_pending', label: 'Stuck in payment' },
  { value: 'active', label: 'Parked' },
  { value: 'recalled', label: 'Held by a register' },
];

const RECOVERY_TONE: Record<
  string,
  'warn' | 'info' | 'negative' | 'positive' | 'neutral'
> = {
  active: 'info',
  payment_pending: 'warn',
  recalled: 'neutral',
  voided: 'negative',
  completed: 'positive',
  recovered: 'positive',
};

/** Admin may assert a payment only on these. `recalled` belongs to a register. */
const FINALIZABLE: RecoveryStatus[] = ['active', 'payment_pending'];
/** Admin may abandon any open record. */
const VOIDABLE: RecoveryStatus[] = ['active', 'payment_pending', 'recalled'];

const TENDER_TYPES = [
  'cash',
  'credit',
  'ebt',
  'wic',
  'check',
  'house',
  'coupon',
  'gc',
  'other',
] as const;
type TenderType = (typeof TENDER_TYPES)[number];

/** Tenders where the card metadata fields are worth showing. */
const CARDLIKE: TenderType[] = ['credit', 'ebt', 'wic', 'gc'];

interface RecoverySearch {
  id?: string;
  status: StatusFilter;
}

export const Route = createFileRoute('/_app/recovery')({
  staticData: { crumb: 'Parked carts' },
  validateSearch: (s: Record<string, unknown>): RecoverySearch => ({
    id: asOptionalText(s.id),
    status: STATUS_OPTIONS.some((o) => o.value === s.status)
      ? (s.status as StatusFilter)
      : 'all',
  }),
  component: RecoveryScreen,
});

function RecoveryScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Inspect' },
  ]);

  function patch(next: Partial<RecoverySearch>) {
    void navigate({ search: (prev: RecoverySearch) => ({ ...prev, ...next }) });
  }

  const listParams = {
    status: search.status === 'all' ? undefined : search.status,
    limit: 200,
  };

  const list = useQuery({
    queryKey: ['recovery', 'list', listParams],
    // Not the paginated envelope — this endpoint answers `{rows, total}`.
    queryFn: () =>
      api.get<AdminRecoveryListResponse>('/admin/recovery', { params: listParams }),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });

  const rows = list.data?.rows;

  const totals = useMemo(() => {
    const all = rows ?? [];
    return {
      count: all.length,
      value: all.reduce((sum, r) => sum + r.totalCents, 0),
      stuck: all.filter((r) => r.status === 'payment_pending').length,
      held: all.filter((r) => r.status === 'recalled').length,
    };
  }, [rows]);

  const columns: Array<Column<AdminRecoveryRow>> = [
    {
      id: 'suspendedAt',
      header: 'Parked',
      width: '5.5rem',
      mono: true,
      cell: (row) => (
        <span title={dateTime(row.suspendedAt)}>{smartDate(row.suspendedAt)}</span>
      ),
    },
    {
      id: 'status',
      header: 'State',
      width: '8rem',
      cell: (row) => (
        <Badge
          tone={RECOVERY_TONE[row.status ?? ''] ?? 'neutral'}
          dot
          pulse={row.status === 'payment_pending'}
        >
          {row.status === 'payment_pending'
            ? 'Stuck in payment'
            : row.status === 'recalled'
              ? 'Held by register'
              : humanize(row.status ?? 'unknown')}
        </Badge>
      ),
    },
    {
      id: 'register',
      header: 'Register',
      width: '7rem',
      hideBelow: 'md',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">
          {row.registerName ?? `Register ${row.registerId}`}
        </span>
      ),
    },
    {
      id: 'cashier',
      header: 'Cashier',
      width: '7rem',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">{row.cashierName ?? '—'}</span>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (row) => (
        <span className="text-ink truncate text-xs">
          {row.customerDisplayName || row.customerName || 'Walk-in'}
        </span>
      ),
    },
    {
      id: 'itemCount',
      header: 'Items',
      width: '3.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (row) => num(row.itemCount),
    },
    {
      id: 'totalCents',
      header: 'Cart',
      width: '6rem',
      align: 'right',
      mono: true,
      cell: (row) => money(row.totalCents),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Parked carts"
        subtitle="Carts a register parked or left mid-payment"
      />

      <Toolbar>
        <ChipGroup<StatusFilter>
          value={search.status}
          onChange={(v) => patch({ status: v, id: undefined })}
          options={STATUS_OPTIONS}
        />
      </Toolbar>

      <div className="border-line shrink-0 border-b px-4 py-3">
        <StatRow className="grid-cols-2 md:grid-cols-4 xl:grid-cols-4">
          <Stat label="Open carts" value={num(totals.count)} />
          <Stat
            label="Stuck in payment"
            value={num(totals.stuck)}
            tone={totals.stuck > 0 ? 'warn' : undefined}
            sub="Outcome unknown at the register"
          />
          <Stat label="Held by a register" value={num(totals.held)} />
          <Stat label="Value parked" value={money(totals.value)} />
        </StatRow>
      </div>

      <PageBody bleed className="min-h-0 overflow-hidden">
        <SplitView
          storageKey="recovery"
          defaultDetail={520}
          maxDetail={820}
          onCloseDetail={() => patch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<LifeBuoy />}
              title="No cart selected"
              description="Pick a row to read the cart, the money already taken, and what is still owed."
            />
          }
          // Keyed by id: restock decisions and half-typed reasons must never
          // survive a change of selection.
          detail={
            search.id ? <RecoveryInspector key={search.id} id={search.id} /> : undefined
          }
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              onRowOpen={(row) => patch({ id: row.id })}
              isRowActive={(row) => row.id === search.id}
              rowTone={(row) => (row.status === 'payment_pending' ? 'warn' : null)}
              empty={{
                icon: <LifeBuoy />,
                title: 'Nothing stranded',
                description: 'No register is holding a parked or half-paid cart.',
              }}
              footer={<span>{num(list.data?.total ?? 0)} open records</span>}
            />
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

export function RecoveryInspector({ id }: { id: string }) {
  const valid = isUuid(id);
  const isManager = useAtLeast('manager');

  const detail = useQuery({
    queryKey: ['recovery', id],
    queryFn: () => api.get<AdminRecoveryRow>(`/admin/recovery/${id}`),
    enabled: valid,
  });

  if (!valid) {
    return (
      <div className="p-3">
        <EmptyState
          compact
          title="That recovery record is unavailable"
          description={`“${id}” is not a recovery id, so nothing was requested. Clear the selection and pick a row.`}
        />
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div className="p-3">
        <ErrorState
          title="Could not load this recovery record"
          message={errorMessage(detail.error)}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  return (
    <RecoveryDetail
      key={recoverySnapshotKey(detail.data)}
      row={detail.data}
      isManager={isManager}
    />
  );
}

function RecoveryDetail({
  row,
  isManager,
}: {
  row: AdminRecoveryRow;
  isManager: boolean;
}) {
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeLookup, setFinalizeLookup] = useState<RecoveryLookupResponse | null>(
    null
  );
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const lookupEnabled = isManager && FINALIZABLE.includes(row.status as RecoveryStatus);
  const lookup = useQuery({
    queryKey: ['recovery', 'lookup', row.id, recoverySnapshotKey(row)],
    queryFn: ({ signal }) =>
      api.post<RecoveryLookupResponse>(
        `/admin/recovery/${row.id}/lookup`,
        {},
        { signal, timeout: 6500 }
      ),
    enabled: lookupEnabled,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  function openVoid(reason: string) {
    setFinalizeOpen(false);
    setVoidReason(reason);
    setVoidOpen(true);
  }

  function openFinalize() {
    // Reuse the read already started on open; a slow check cannot hold up manual
    // recovery. The server checks verified payments again on final submission.
    // Keep this draft fixed so late lookup replies never edit manager input.
    setFinalizeLookup(lookup.isError ? null : (lookup.data ?? null));
    setVoidOpen(false);
    setFinalizeOpen(true);
  }

  const cart = parseRecoveryCart(row.cartJson);
  const applied = parseRecoveryTenders(row.appliedTendersJson);
  const appliedTenders = applied.ok ? applied.value : [];
  const balanceDue = recoveryBalanceDue(row.totalCents, appliedTenders);
  const returnLines = cart.ok ? cart.value.filter((l) => l.lineType === 'return') : [];
  const snapshotReadable = cart.ok && applied.ok;

  const canFinalize =
    isManager && snapshotReadable && FINALIZABLE.includes(row.status as RecoveryStatus);
  const canVoid = VOIDABLE.includes(row.status as RecoveryStatus);

  return (
    <div className="space-y-3 p-3">
      <div>
        <div className="flex items-center gap-2">
          <Badge
            tone={RECOVERY_TONE[row.status ?? ''] ?? 'neutral'}
            dot
            pulse={row.status === 'payment_pending'}
          >
            {row.status === 'payment_pending'
              ? 'Stuck in payment'
              : row.status === 'recalled'
                ? 'Held by register'
                : humanize(row.status ?? 'unknown')}
          </Badge>
          <Code copyable>{row.id.slice(0, 8).toUpperCase()}</Code>
        </div>
        <div className="text-2xs text-ink-faint mt-1">
          Parked {dateTime(row.suspendedAt)} on{' '}
          {row.registerName ?? `register ${row.registerId}`} by{' '}
          {row.cashierName ?? `cashier ${row.cashierId}`}
        </div>
      </div>

      {row.status === 'payment_pending' ? (
        <Callout tone="warn" title="The register never learned the outcome">
          Check the source register and processor before recording an outcome. Finalize
          only confirmed payments that settle this cart. Void only after verified no
          capture or a verified completed refund or reversal; Void does not move money.
        </Callout>
      ) : null}

      {lookupEnabled && lookup.data?.status !== 'none' ? (
        <Callout
          title={
            lookup.isFetching
              ? 'Checking Sola…'
              : lookup.isError
                ? 'Sola payment lookup unavailable'
                : lookup.data?.status === 'verified'
                  ? 'Sola approval verified'
                  : lookup.data?.status === 'declined'
                    ? 'Sola shows this attempt declined'
                    : 'Sola payment lookup'
          }
          tone={
            !lookup.isFetching && !lookup.isError && lookup.data?.status === 'verified'
              ? 'positive'
              : 'info'
          }
          actions={
            <Button
              size="xs"
              disabled={lookup.isFetching}
              onClick={() => void lookup.refetch()}
            >
              Check again
            </Button>
          }
        >
          <div aria-live="polite">
            {lookup.isFetching
              ? 'The cart stays open while the payment is checked.'
              : lookup.isError
                ? 'Could not check Sola. Check the processor portal and use manual Finalize if the payment is confirmed.'
                : lookup.data?.message}
          </div>
          {!lookup.isFetching &&
          !lookup.isError &&
          lookup.data?.status === 'verified' &&
          lookup.data.tender ? (
            <p className="mt-1 font-mono tabular-nums">
              {humanize(lookup.data.tender.tenderType)}{' '}
              {money(lookup.data.tender.amountCents)} · ref {lookup.data.tender.reference}
              {lookup.data.tender.lastFour
                ? ` · •••• ${lookup.data.tender.lastFour}`
                : ''}
            </p>
          ) : null}
          {!lookup.isFetching &&
          !lookup.isError &&
          lookup.data?.status === 'declined' &&
          lookup.data.voidReason ? (
            <div className="mt-2 space-y-2">
              <p>
                This result is for this attempt only. Review other payments below and
                check the source register before closing the cart.
              </p>
              <Button
                size="xs"
                variant="danger"
                onClick={() => {
                  openVoid(lookup.data?.voidReason ?? '');
                }}
              >
                Review Void with evidence
              </Button>
              {canFinalize ? (
                <Button size="xs" onClick={openFinalize}>
                  Enter a separately confirmed payment
                </Button>
              ) : null}
            </div>
          ) : null}
        </Callout>
      ) : null}

      {row.status === 'recalled' ? (
        <Callout tone="info" title="A register is holding this cart">
          It can be voided from here, but it cannot be finalized: asserting a payment on a
          cart a lane is actively working is the one transition the lifecycle refuses to
          an admin.
        </Callout>
      ) : null}

      {row.recoveredTransactionId ? (
        <Callout
          tone="positive"
          title="Already turned into a transaction"
          actions={
            <Button size="xs" icon={<ExternalLink className="size-3" />} asChild>
              <Link to="/transactions/$id" params={{ id: row.recoveredTransactionId }}>
                Open
              </Link>
            </Button>
          }
        >
          Receipt{' '}
          <span className="font-mono">
            {row.recoveredTransactionId.slice(0, 8).toUpperCase()}
          </span>{' '}
          already carries this cart. Do not finalize it again.
        </Callout>
      ) : null}

      {!cart.ok ? (
        <Callout tone="negative" title="The cart snapshot cannot be read">
          {cart.error} Finalizing is blocked — the server would refuse it anyway, and an
          unreadable cart must never be settled on trust.
        </Callout>
      ) : null}
      {!applied.ok ? (
        <Callout tone="negative" title="The applied-tender snapshot cannot be read">
          {applied.error} The balance due below cannot be trusted.
        </Callout>
      ) : null}

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Cart total" value={money(row.totalCents)} />
        <Stat label="Already applied" value={money(row.totalCents - balanceDue)} />
        <Stat
          label="Balance due"
          value={applied.ok ? money(balanceDue) : '—'}
          tone={balanceDue > 0 ? 'warn' : 'positive'}
        />
      </div>

      <Panel
        title="Cart"
        description={`${num(row.itemCount)} recorded · ${num(cart.ok ? cart.value.length : 0)} lines in the snapshot`}
        bodyClassName="p-3 pt-1"
      >
        {cart.ok ? <CartLines lines={cart.value} /> : null}
      </Panel>

      <Panel title="Money already taken" bodyClassName="p-3 pt-1">
        {appliedTenders.length === 0 ? (
          <p className="text-ink-faint py-1 text-xs">
            No tender has been applied to this cart.
          </p>
        ) : (
          <ul className="divide-line-soft divide-y">
            {appliedTenders.map((t) => (
              <li key={t.index} className="flex items-start gap-3 py-1.5">
                <span className="min-w-0 flex-1">
                  <span className="text-ink text-xs font-medium">{humanize(t.type)}</span>
                  <span className="text-2xs text-ink-faint mt-0.5 flex flex-wrap gap-2">
                    {t.last4 ? <span>•••• {t.last4}</span> : null}
                    {t.authCode ? <span>auth {t.authCode}</span> : null}
                    {t.entryMethod ? <span>{humanize(t.entryMethod)}</span> : null}
                    {t.reference ? <Code copyable>{t.reference}</Code> : null}
                  </span>
                </span>
                <span className="text-ink shrink-0 font-mono text-xs tabular-nums">
                  {money(t.applied)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="border-line mt-2 border-t pt-2">
          <AmountRow label="Cart total" value={row.totalCents} />
          <AmountRow label="Applied" value={row.totalCents - balanceDue} tone="muted" />
          <AmountRow label="Balance due" value={balanceDue} strong />
        </div>
      </Panel>

      <Panel title="Record">
        <DetailList columns={2}>
          <Detail label="Customer">
            {row.customerDisplayName || row.customerName || 'Walk-in'}
          </Detail>
          <Detail label="Account" mono>
            {row.customerAccountNumber ?? '—'}
          </Detail>
          <Detail label="EBT eligible" mono>
            {row.ebtEligibleCents === null ? '—' : money(row.ebtEligibleCents)}
          </Detail>
          <Detail label="Client id" mono>
            {row.clientRecoveryUuid ? (
              <Code copyable>{row.clientRecoveryUuid.slice(0, 8).toUpperCase()}</Code>
            ) : (
              <span className="text-warn">missing</span>
            )}
          </Detail>
          {row.closedAt ? <Detail label="Closed">{dateTime(row.closedAt)}</Detail> : null}
          {row.closedReason ? (
            <Detail label="Closed reason">{row.closedReason}</Detail>
          ) : null}
        </DetailList>
      </Panel>

      {!row.clientRecoveryUuid && FINALIZABLE.includes(row.status as RecoveryStatus) ? (
        <Callout tone="negative" title="This record has no stable client id">
          Without it the server cannot deduplicate a replay, so finalize will be refused.
          Void it and re-ring the sale instead.
        </Callout>
      ) : null}

      <div className="flex gap-2">
        {canFinalize ? (
          <Button
            variant="primary"
            size="sm"
            className="flex-1 justify-center"
            icon={<Wallet className="size-3.5" />}
            onClick={openFinalize}
          >
            Finalize
          </Button>
        ) : null}
        {canVoid ? (
          <Button
            variant="danger"
            size="sm"
            className="flex-1 justify-center"
            icon={<Ban className="size-3.5" />}
            onClick={() => {
              openVoid('');
            }}
          >
            Void cart
          </Button>
        ) : null}
      </div>

      {!isManager && FINALIZABLE.includes(row.status as RecoveryStatus) ? (
        <Callout tone="info" title="Finalizing needs a manager">
          Turning a parked cart into a real transaction moves money, so it starts at
          manager. Voiding is available to anyone with recovery access.
        </Callout>
      ) : null}

      {canFinalize && finalizeOpen ? (
        <FinalizeDialog
          open={finalizeOpen}
          onOpenChange={setFinalizeOpen}
          row={row}
          returnLines={returnLines}
          balanceDue={balanceDue}
          lookupResult={finalizeLookup}
        />
      ) : null}

      {canVoid && voidOpen ? (
        <VoidDialog
          open={voidOpen}
          onOpenChange={setVoidOpen}
          row={row}
          appliedCents={row.totalCents - balanceDue}
          initialReason={voidReason}
        />
      ) : null}
    </div>
  );
}

function CartLines({ lines }: { lines: RecoveryCartLine[] }) {
  if (lines.length === 0) {
    return <p className="text-ink-faint py-1 text-xs">The cart snapshot is empty.</p>;
  }
  return (
    <ul className="divide-line-soft divide-y">
      {lines.map((line) => (
        <li key={line.index} className="flex items-start gap-3 py-1.5">
          <span className="text-ink-muted w-12 shrink-0 text-right font-mono text-xs tabular-nums">
            {fmtQty(line.quantity)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-ink truncate text-xs">{line.description}</span>
              {line.lineType === 'return' ? <Badge tone="warn">Return</Badge> : null}
            </span>
            <span className="text-2xs text-ink-faint mt-0.5 flex flex-wrap gap-2">
              <span className="font-mono tabular-nums">{money(line.unitPrice)} ea</span>
              {line.tax ? (
                <span className="font-mono tabular-nums">tax {money(line.tax)}</span>
              ) : null}
              {line.barcode ? <Code copyable>{line.barcode}</Code> : null}
            </span>
          </span>
          <span className="text-ink shrink-0 font-mono text-xs tabular-nums">
            {money(line.total)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ── Finalize ─────────────────────────────────────────────────────────────── */

interface CustomerPick {
  id: number | string;
  name: string;
  companyName?: string | null;
  accountNumber?: string | null;
  balance: number;
  hasCredit?: boolean | null;
}

type FinalizeOutcome =
  | {
      kind: 'finalized';
      transactionId: string;
      inventoryAdjustments: number;
      tenderCount: number;
    }
  /** `completed`: the recovery record is terminal, but its returned transaction
   *  id is not verified here as an owner-completed sale;
   *  `transaction`: a transaction uses the cart UUID but its status/owner is
   *  unknown; `admin`: another manager or tab finalized it first. */
  | {
      kind: 'already';
      transactionId: string;
      via: 'completed' | 'transaction' | 'admin';
    };

function FinalizeDialog({
  open,
  onOpenChange,
  row,
  returnLines,
  balanceDue,
  lookupResult,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: AdminRecoveryRow;
  returnLines: RecoveryCartLine[];
  balanceDue: number;
  lookupResult: RecoveryLookupResponse | null;
}) {
  const parsedApplied = parseRecoveryTenders(row.appliedTendersJson);
  const hasAppliedCard =
    parsedApplied.ok &&
    parsedApplied.value.some((tender) =>
      ['credit', 'debit', 'ebt', 'ebt_cash', 'wic'].includes(tender.type)
    );
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [verified, setVerified] = useState(() => verifiedRecoveryLookup(lookupResult));
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [tenderType, setTenderType] = useState<TenderType>(
    verified?.tender.tenderType ?? 'cash'
  );
  const [amount, setAmount] = useState<number | null>(
    verified?.tender.amountCents ?? balanceDue
  );
  const [reference, setReference] = useState(verified?.tender.reference ?? '');
  const [authCode, setAuthCode] = useState('');
  const [lastFour, setLastFour] = useState(verified?.tender.lastFour ?? '');
  const [entryMethod, setEntryMethod] = useState(verified?.tender.entryMethod ?? '');
  const [reason, setReason] = useState(
    verified ? `Sola confirmed ref ${verified.tender.reference}.` : ''
  );
  const [customer, setCustomer] = useState<CustomerPick | null>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const debouncedCustomer = useDebouncedValue(customerQuery);
  // Deliberately starts empty: a restock decision is a judgement about physical
  // goods, so there is no safe default to pre-tick.
  const [restock, setRestock] = useState<Record<number, boolean>>({});
  const [outcome, setOutcome] = useState<FinalizeOutcome | null>(null);

  const customers = useQuery({
    queryKey: ['customers', 'recovery-search', debouncedCustomer],
    queryFn: () =>
      api.get<PaginatedResponse<CustomerPick>>('/customers', {
        params: { search: debouncedCustomer, pageSize: 8, sort: 'name', order: 'asc' },
      }),
    enabled: open && tenderType === 'house' && debouncedCustomer.trim().length >= 2,
  });

  /** The account a house tender lands on: the picked one, else the cart's own. */
  const houseCustomerId =
    tenderType === 'house'
      ? customer
        ? Number(customer.id)
        : row.customerId === null
          ? null
          : Number(row.customerId)
      : null;

  const finalize = useMutation({
    mutationFn: async (): Promise<FinalizeOutcome> => {
      const body = {
        processorMatch: verified?.processorMatch,
        tenderType,
        amountCents: amount ?? 0,
        // Only sent for a house tender, and only when it resolved to a real
        // account id — never a coerced 0.
        customerId: houseCustomerId ?? undefined,
        reference: reference.trim() || undefined,
        authCode: authCode.trim() || undefined,
        lastFour: lastFour.trim() || undefined,
        entryMethod: entryMethod.trim() || undefined,
        reason: reason.trim(),
        restockDecisions: returnLines.map((line) => ({
          lineIndex: line.index,
          restock: restock[line.index] === true,
        })),
      };

      try {
        const result = await api.post<FinalizeRecoveryResponse>(
          `/admin/recovery/${row.id}/finalize`,
          body
        );
        return {
          kind: 'finalized',
          transactionId: result.recoveredTransactionId ?? result.transactionId,
          inventoryAdjustments: result.inventoryAdjustments,
          tenderCount: result.tenderCount,
        };
      } catch (err) {
        // A recognized 409 means this request did not move money. Show either
        // the terminal result or a reconciliation stop; never invite a retry.
        if (err instanceof ApiError && err.status === 409) {
          const conflict = parseRecoveryLifecycleConflict(err.body);
          const completedTransactionId = completedRecoveryTransactionId(conflict);
          if (completedTransactionId) {
            return {
              kind: 'already',
              transactionId: completedTransactionId,
              via: 'completed',
            };
          }
          // A UUID collision can be a refund or a transaction owned by a
          // different register. Its existence alone does not prove a sale or
          // that sync will close this record.
          if (
            conflict.reason === 'transaction_exists' &&
            conflict.recoveredTransactionId
          ) {
            return {
              kind: 'already',
              transactionId: conflict.recoveredTransactionId,
              via: 'transaction',
            };
          }
          // Any other 409 (another manager's `recovered`, for one): re-read the
          // record and let it answer.
          const fresh = await api
            .get<AdminRecoveryRow>(`/admin/recovery/${row.id}`)
            .catch(() => null);
          if (fresh?.recoveredTransactionId) {
            return {
              kind: 'already',
              transactionId: fresh.recoveredTransactionId,
              via: 'admin',
            };
          }
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      setOutcome(result);
      if (result.kind === 'already' && result.via === 'completed') {
        toast.info('Recovery record already completed', {
          description: `The server returned linked transaction ${result.transactionId}; verify it before treating it as a sale. No money moved now.`,
        });
      } else if (result.kind === 'already' && result.via === 'transaction') {
        toast.info('Transaction conflict — reconciliation required', {
          description: `Transaction ${result.transactionId} already uses this cart ID. The record remains open because the server cannot confirm an owner-completed sale. Do not retry. No money moved now.`,
        });
      } else if (result.kind === 'already') {
        toast.info('Already finalized', {
          description: 'This cart had already become a transaction. No money moved now.',
        });
      } else {
        toast.success('Cart finalized', {
          description: `${money(amount ?? 0)} recorded as ${humanize(tenderType)} · ${num(result.inventoryAdjustments)} inventory adjustments.`,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['recovery'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (err) => {
      // Never turn a rejected processor match into a manual assertion silently.
      // The manager must check again or explicitly enter a confirmed payment.
      setVerificationFailed(true);
      toast.error('The server refused this finalize', {
        description: errorMessage(err),
      });
      setStage('form');
    },
  });

  function close() {
    onOpenChange(false);
    setStage('form');
    setOutcome(null);
  }

  const trimmedReason = reason.trim();
  const undecided = returnLines.filter((l) => restock[l.index] === undefined);
  const restockCount = returnLines.filter((l) => restock[l.index] === true).length;

  const amountMatches = amount !== null && amount === balanceDue;
  const lastFourValid = lastFour.trim() === '' || /^\d{4}$/.test(lastFour.trim());
  const ebtBlocked = tenderType === 'ebt' && (row.ebtEligibleCents ?? 0) <= 0;

  const ready =
    (!verified || !verificationFailed) &&
    amount !== null &&
    amount > 0 &&
    amountMatches &&
    trimmedReason.length >= 1 &&
    trimmedReason.length <= 500 &&
    undecided.length === 0 &&
    lastFourValid &&
    !ebtBlocked &&
    (tenderType !== 'house' || (houseCustomerId !== null && houseCustomerId > 0));

  const consequences = [
    `Creates a real transaction for ${money(row.totalCents)} and closes this record as recovered.`,
    verified
      ? `Records ${money(amount ?? 0)} ${humanize(tenderType).toLowerCase()}, ref ${verified.tender.reference}, confirmed by Sola. No card is charged.`
      : `Records ${money(amount ?? 0)} as ${humanize(tenderType).toLowerCase()}. You confirm this payment was received.`,
    returnLines.length === 0
      ? 'No return lines in this cart, so no stock goes back.'
      : `Restocks ${num(restockCount)} of ${num(returnLines.length)} return ${returnLines.length === 1 ? 'line' : 'lines'}; the rest move no stock.`,
    tenderType === 'house'
      ? `Posts ${money(amount ?? 0)} to ${customer?.name ?? row.customerDisplayName ?? 'the house account'}.`
      : 'No house account is touched.',
    'There is no undo. A correction means a refund on the register.',
  ];

  /* Once it has landed, the dialog stops being a form and becomes a receipt of
     what happened — nothing here can be resubmitted. */
  if (outcome) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={
          outcome.kind === 'already'
            ? outcome.via === 'completed'
              ? 'Recovery record already completed'
              : outcome.via === 'transaction'
                ? 'Transaction conflict'
                : 'Already finalized'
            : 'Cart finalized'
        }
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Close
            </Button>
            <Button variant="primary" asChild>
              <Link to="/transactions/$id" params={{ id: outcome.transactionId }}>
                Open transaction
              </Link>
            </Button>
          </>
        }
      >
        <Callout
          tone={outcome.kind === 'already' ? 'info' : 'positive'}
          title={
            outcome.kind === 'already'
              ? 'No money moved just now'
              : `${money(amount ?? 0)} recorded as ${humanize(tenderType).toLowerCase()}`
          }
        >
          {outcome.kind === 'already'
            ? outcome.via === 'completed'
              ? 'This recovery record already has completed status. The transaction below was returned with the conflict but is not verified here as the owner’s completed sale. Verify it before acting. No money moved just now.'
              : outcome.via === 'transaction'
                ? 'A transaction already uses this cart ID, but the server cannot confirm that it is the owner’s completed sale. The recovery record remains open and needs reconciliation. Do not retry finalize or void. No money moved just now.'
                : 'The server reported this record was already turned into a transaction — most likely by another tab or another manager. Nothing was charged a second time.'
            : `${num(outcome.tenderCount)} tender${outcome.tenderCount === 1 ? '' : 's'} and ${num(outcome.inventoryAdjustments)} inventory adjustment${outcome.inventoryAdjustments === 1 ? '' : 's'} were written.`}
          <div className="text-2xs mt-1.5 font-mono">
            {outcome.transactionId.toUpperCase()}
          </div>
        </Callout>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open && stage === 'form'}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Finalize this cart"
        description="Creates a transaction and records the payment. Manager-only, and not reversible from here."
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!ready}
              onClick={() => setStage('confirm')}
            >
              Review consequences
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Consequences
            tone="info"
            items={[
              <>
                Balance due is{' '}
                <span className="font-mono tabular-nums">{money(balanceDue)}</span> — the
                tender has to settle it exactly, or the server refuses.
              </>,
            ]}
          />

          {verified ? (
            <Callout
              tone={verificationFailed ? 'warn' : 'positive'}
              title={
                verificationFailed
                  ? 'Sola match needs another check'
                  : 'Payment confirmed by Sola'
              }
              actions={
                <Button
                  size="xs"
                  onClick={() => {
                    if (reason === `Sola confirmed ref ${verified.tender.reference}.`) {
                      setReason('');
                    }
                    setVerified(null);
                  }}
                >
                  Enter manually
                </Button>
              }
            >
              {verificationFailed
                ? 'Close Finalize and use Check again, or enter a payment you confirmed yourself.'
                : 'Sola confirmed these payment details. Finalize checks them again before saving. No card is charged.'}
            </Callout>
          ) : (
            <Callout tone="warn" title="Manual payment entry">
              {lookupResult?.status === 'declined'
                ? 'Sola reports this attempt declined. Use Void after reviewing the evidence, or enter a separately confirmed payment.'
                : CARDLIKE.includes(tenderType) || hasAppliedCard
                  ? 'Check the source register and processor portal. Only record a payment you have confirmed was received.'
                  : 'Check the source register. Only record a payment you have confirmed was received.'}
            </Callout>
          )}

          {hasAppliedCard || verified ? (
            <p className="text-sm text-ink-muted">
              Check the register and the card processor before finishing a sale that has
              card payments.
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tender type" required>
              <Select
                size="sm"
                value={tenderType}
                disabled={verified !== null}
                onChange={(e) => setTenderType(e.currentTarget.value as TenderType)}
              >
                {TENDER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {humanize(t)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Amount"
              required
              error={
                amount === null || amount <= 0
                  ? 'Enter the amount taken.'
                  : !amountMatches
                    ? `Must equal the balance due, ${money(balanceDue)}.`
                    : null
              }
            >
              <MoneyInput
                size="sm"
                value={amount}
                onValueChange={setAmount}
                disabled={verified !== null}
              />
            </Field>
          </div>

          {ebtBlocked ? (
            <Callout tone="negative" title="This cart is not EBT-eligible">
              No EBT-eligible total was recorded against it, so an EBT tender will be
              refused.
            </Callout>
          ) : null}

          {tenderType === 'house' ? (
            <Field
              label="House account"
              required
              hint={
                row.customerId !== null
                  ? 'Defaults to the customer the cart was rung against. Search to charge a different account.'
                  : 'This cart has no customer, so one must be chosen.'
              }
              error={
                houseCustomerId === null || houseCustomerId <= 0
                  ? 'A house tender needs an account to charge.'
                  : null
              }
            >
              <div className="space-y-2">
                <div className="border-line bg-surface-sunken text-ink rounded border px-2.5 py-1.5 text-xs">
                  {customer
                    ? `${customer.companyName || customer.name} · balance ${money(customer.balance)}`
                    : (row.customerDisplayName ??
                      row.customerName ??
                      'No account selected')}
                </div>
                <SearchInput
                  size="sm"
                  value={customerQuery}
                  onValueChange={setCustomerQuery}
                  placeholder="Search another account…"
                />
                {debouncedCustomer.trim().length >= 2 ? (
                  <div className="border-line max-h-40 overflow-y-auto rounded border">
                    {customers.isLoading ? (
                      <div className="p-2">
                        <Skeleton className="h-5 w-full" />
                      </div>
                    ) : (customers.data?.data ?? []).length === 0 ? (
                      <p className="text-ink-faint p-2 text-xs">No match.</p>
                    ) : (
                      <ul className="divide-line-soft divide-y">
                        {(customers.data?.data ?? []).map((c) => (
                          <li key={String(c.id)}>
                            <button
                              type="button"
                              onClick={() => setCustomer(c)}
                              className="hover:bg-surface-hover flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs"
                            >
                              <span className="text-ink min-w-0 flex-1 truncate">
                                {c.companyName || c.name}
                              </span>
                              {c.accountNumber ? <Code>{c.accountNumber}</Code> : null}
                              <span className="text-ink-muted shrink-0 font-mono tabular-nums">
                                {money(c.balance)}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            </Field>
          ) : null}

          {CARDLIKE.includes(tenderType) ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Auth code">
                <Input
                  size="sm"
                  mono
                  value={authCode}
                  readOnly={verified !== null}
                  onChange={(e) => setAuthCode(e.currentTarget.value)}
                />
              </Field>
              <Field
                label="Last four"
                error={lastFourValid ? null : 'Four digits, or leave empty.'}
              >
                <Input
                  size="sm"
                  mono
                  inputMode="numeric"
                  maxLength={4}
                  value={lastFour}
                  readOnly={verified !== null}
                  onChange={(e) => setLastFour(e.currentTarget.value)}
                />
              </Field>
              <Field label="Entry method">
                <Input
                  size="sm"
                  value={entryMethod}
                  readOnly={verified !== null}
                  onChange={(e) => setEntryMethod(e.currentTarget.value)}
                  placeholder="manual_recovery"
                />
              </Field>
            </div>
          ) : null}

          <Field label="Reference" hint="Processor reference or cheque number, if any.">
            <Input
              size="sm"
              mono
              value={reference}
              readOnly={verified !== null}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
          </Field>

          {returnLines.length > 0 ? (
            <Field
              label={`Restock decisions · ${num(returnLines.length)} return ${returnLines.length === 1 ? 'line' : 'lines'}`}
              required
              error={
                undecided.length > 0
                  ? `${num(undecided.length)} line${undecided.length === 1 ? '' : 's'} still undecided.`
                  : null
              }
              hint="Every return line needs an answer. There is no default — this decides whether stock goes back on the shelf."
            >
              <ul className="divide-line-soft border-line divide-y rounded border">
                {returnLines.map((line) => {
                  const choice = restock[line.index];
                  return (
                    <li
                      key={line.index}
                      className="flex items-center gap-3 px-2.5 py-1.5"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="text-ink block truncate text-xs">
                          {line.description}
                        </span>
                        <span className="text-2xs text-ink-faint font-mono tabular-nums">
                          {fmtQty(line.quantity)} × {money(line.unitPrice)}
                        </span>
                      </span>
                      <span className="flex shrink-0 gap-1">
                        <RestockChoice
                          active={choice === true}
                          tone="positive"
                          onClick={() =>
                            setRestock((prev) => ({ ...prev, [line.index]: true }))
                          }
                        >
                          Restock
                        </RestockChoice>
                        <RestockChoice
                          active={choice === false}
                          tone="negative"
                          onClick={() =>
                            setRestock((prev) => ({ ...prev, [line.index]: false }))
                          }
                        >
                          Scrap
                        </RestockChoice>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Field>
          ) : null}

          <Field
            label="Reason"
            required
            hint={`${trimmedReason.length}/500 · recorded in the audit log`}
            error={
              trimmedReason.length > 500 ? 'Too long — 500 characters maximum.' : null
            }
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="Card captured at the processor; register lost the response"
            />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && stage === 'confirm'}
        onOpenChange={(next) => {
          if (!next) setStage('form');
        }}
        title={verified ? 'Record the Sola-confirmed payment' : 'Record this payment'}
        description="The cart closes only after its payment and inventory changes have been saved."
        confirmLabel={`Finalize ${money(amount ?? 0)}`}
        typeToConfirm="FINALIZE"
        consequences={consequences}
        loading={finalize.isPending}
        onConfirm={() => finalize.mutate()}
      />
    </>
  );
}

function RestockChoice({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone: 'positive' | 'negative';
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-2xs h-6 rounded-sm border px-2 transition-colors duration-75',
        active &&
          tone === 'positive' &&
          'border-positive bg-positive-muted text-positive',
        active &&
          tone === 'negative' &&
          'border-negative bg-negative-muted text-negative',
        !active &&
          'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink'
      )}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

/* ── Void ─────────────────────────────────────────────────────────────────── */

function VoidDialog({
  open,
  onOpenChange,
  row,
  appliedCents,
  initialReason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: AdminRecoveryRow;
  appliedCents: number;
  initialReason: string;
}) {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [reason, setReason] = useState(initialReason);

  const voidCart = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/admin/recovery/${row.id}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Cart voided', {
        description:
          'Recovery closed. No money moved; retained payment evidence stays available on the source register.',
      });
      void queryClient.invalidateQueries({ queryKey: ['recovery'] });
      void queryClient.invalidateQueries({ queryKey: ['badges'] });
      onOpenChange(false);
      setStage('form');
      setReason('');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const conflict = parseRecoveryLifecycleConflict(err.body);
        if (conflict.reason === 'canonical_transaction_exists') {
          toast.info('Transaction conflict — reconciliation required', {
            description: `Transaction ${conflict.recoveredTransactionId ?? '(id unavailable)'} already uses this cart ID. The record remains open because the server cannot confirm an owner-completed sale. Do not retry finalize or void. No money moved now.`,
          });
        } else if (
          conflict.reason === 'illegal_transition' &&
          conflict.currentStatus === 'completed'
        ) {
          toast.info('Recovery record already completed', {
            description: conflict.recoveredTransactionId
              ? `The server returned linked transaction ${conflict.recoveredTransactionId}; verify it before treating it as a sale. No money moved now.`
              : 'This recovery record already has completed status. No money moved now.',
          });
        } else {
          toast.info('This record already moved on', {
            description: `${errorMessage(err)} — refreshed with the current state.`,
          });
        }
        void queryClient.invalidateQueries({ queryKey: ['recovery'] });
        onOpenChange(false);
        setStage('form');
      } else {
        toast.error('Could not void this cart', { description: errorMessage(err) });
        setStage('form');
      }
    },
  });

  const trimmed = reason.trim();
  const hasMoney = appliedCents > 0;
  const reasonOk = trimmed.length >= 1 && trimmed.length <= 500;

  function close() {
    onOpenChange(false);
    setStage('form');
    setReason('');
  }

  return (
    <>
      {/* The reason is mandatory server-side, so it is collected before the
          confirmation rather than leaving a confirm button that silently
          refuses to act. */}
      <Dialog
        open={open && stage === 'form'}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Void this cart"
        description="Close after verified no capture or a verified completed refund or reversal. This action moves no money."
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!reasonOk}
              onClick={() => setStage('confirm')}
            >
              Review consequences
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {hasMoney ? (
            <Callout tone="negative" title="Money has already been applied here">
              This cart records {money(appliedCents)} in payments. Check the source
              register and processor, then record the verified no-capture or completed
              refund/reversal outcome. Voiding does not refund these payments.
            </Callout>
          ) : (
            <Callout tone="warn" title="A missing payment is not proof of no capture">
              Check the source register and processor even when no payment appears here.
              An unanswered request can still have moved money.
            </Callout>
          )}
          <Field
            label="Reason"
            required
            hint={`${trimmed.length}/500 · stored on the closed record`}
            error={trimmed.length > 500 ? 'Too long — 500 characters maximum.' : null}
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="Verified outcome, processor reference and action taken"
            />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && stage === 'confirm'}
        onOpenChange={(next) => {
          if (!next) setStage('form');
        }}
        title="Void this cart"
        description="Closes the recovery record permanently without moving money or deleting retained payment evidence."
        confirmLabel="Void cart"
        // Applied funds require an explicit confirmation of prior reconciliation.
        // This action itself performs no reversal or refund.
        typeToConfirm={hasMoney ? 'VOID' : undefined}
        consequences={[
          `Closes ${money(row.totalCents)} of parked cart without creating a transaction.`,
          hasMoney
            ? `${money(appliedCents)} is recorded here. Confirm verified no capture or a completed refund/reversal before closing.`
            : 'No recorded payment does not prove no capture. Check the source register and processor.',
          'No money moves. Retained payment evidence remains on the source register.',
          'Cannot be undone. Do not ring this sale again while any payment outcome is uncertain.',
        ]}
        loading={voidCart.isPending}
        onConfirm={() => voidCart.mutate()}
      />
    </>
  );
}
